import { useState } from "react";

import {
    SelectInput,
    SelectOptionGroupType,
} from "@impero-com/common/components/form/fields/selectInput";
import { t } from "@impero-com/common/utils/i18n";
import { RefCallBack } from "react-hook-form";

import { ControlWithoutFieldsGraphItemDtoListDto as ControlWithoutFieldsListGraphDto } from "api/generated/model";
import { InputSkeleton } from "components/form/skeleton/inputSkeleton";
import { useControlsQuery } from "org/control/queries/useControlsQuery";
import { Guid } from "utils/guid";

import * as theme from "./theme.css";

export interface ControlIdAndName {
    id: Guid;
    name: string;
}
export interface ControlTypeaheadBaseProps {
    helpText?: string;
    label?: string;
    placeholder?: string;
    injectedControls?: ControlIdAndName[];
}
export interface ControlTypeaheadProps extends ControlTypeaheadBaseProps {
    value: Guid | undefined;
    onChange: (value: Guid) => void;
    onBlur?: () => void;
    error?: string;
    disabled?: boolean;
    fieldRef?: RefCallBack;
}
export function ControlTypeahead({
    injectedControls,
    fieldRef,
    error,
    onChange,
    onBlur,
    value,
    disabled,
    helpText,
    placeholder = t("Select_Control_Default_Placeholder"),
    label = t("Select_Control_Default_Label"),
}: ControlTypeaheadProps) {
    const { data, isLoading, error: controlsQueryError } = useControlsQuery();

    if (!data) {
        return (
            <InputSkeleton
                label={label}
                error={controlsQueryError?.message}
                enableAnimation={isLoading}
            />
        );
    }

    return (
        <InnerControlTypeahead
            injectedControls={injectedControls}
            fieldRef={fieldRef}
            error={error}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            disabled={disabled}
            helpText={helpText}
            placeholder={placeholder}
            label={label}
            data={data}
        />
    );
}
interface InnerControlTypeaheadProps extends ControlTypeaheadProps {
    data: ControlWithoutFieldsListGraphDto;
}

function InnerControlTypeahead({
    injectedControls,
    fieldRef,
    error,
    onChange,
    onBlur,
    value,
    disabled,
    helpText,
    placeholder,
    label,
    data,
}: InnerControlTypeaheadProps) {
    const allOptions = toControlGroupsByProgram(data);
    const [options, setOptions] =
        useState<SelectOptionGroupType<string>[]>(allOptions);

    const isValueInData = data.content.some(
        (control) => control.resource.id === value
    );
    const isValueInjected = injectedControls?.some(
        (control) => control.id === value
    );
    if (!isValueInData && !isValueInjected && value !== undefined) {
        throw new Error(
            `Control with id ${value} not found in data or injected controls`
        );
    }
    const shouldShowInjected =
        injectedControls && !isValueInData && isValueInjected;
    const displayValue = shouldShowInjected
        ? injectedControls.find((control) => control.id === value)!.name
        : value;

    const onAutocomplete = (value: string): void => {
        const lcValue = value.toLowerCase();
        const filteredControlPrograms = allOptions
            .map((program) => {
                return {
                    key: program.key,
                    label: program.label,
                    options: program.options.filter(
                        (control) =>
                            // eslint-disable-next-line @typescript-eslint/no-base-to-string
                            `${program.label.toLowerCase()} ${control.label
                                ?.toString()
                                .toLowerCase()}`.indexOf(lcValue) > -1
                    ),
                };
            })
            .filter((program) => program.options.length > 0);
        setOptions(filteredControlPrograms);
    };

    return (
        <SelectInput<Guid | null>
            helpText={helpText}
            className={theme.selectInput}
            onBlur={onBlur}
            label={label}
            placeholder={placeholder}
            onChange={onChange}
            value={displayValue ?? null}
            options={options}
            error={error}
            disabled={disabled}
            fieldRef={fieldRef}
            dropdownMatchSelectWidth
            showSearch
            onSearch={onAutocomplete}
            filterOption={false}
        />
    );
}

function toControlGroupsByProgram({
    content,
    linkedResources,
}: ControlWithoutFieldsListGraphDto) {
    const options = content.reduce<SelectOptionGroupType<Guid>[]>(
        (acc, control, index, array) => {
            const controlResource = control.resource;

            const hasGroup = acc.some(
                (group) => group.key === controlResource.controlProgramId
            );
            if (hasGroup) {
                return acc;
            }

            const controlProgramResource = linkedResources.controlPrograms.find(
                (controlProgram) =>
                    controlProgram.resource.id ===
                    controlResource.controlProgramId
            );
            if (!controlProgramResource) {
                throw new Error(
                    "Expected to find matching control program resource"
                );
            }
            const options = array
                .filter(
                    (control) =>
                        control.resource.controlProgramId ===
                        controlResource.controlProgramId
                )
                .map((control) => ({
                    value: control.resource.id,
                    label: control.resource.title,
                }))
                .toSorted((a, b) => a.label.localeCompare(b.label));
            return [
                ...acc,
                {
                    key: control.resource.controlProgramId,
                    label: controlProgramResource.resource.name,
                    options,
                },
            ];
        },
        []
    );
    return options.toSorted((a, b) => a.label.localeCompare(b.label));
}
